import React, { FC, useEffect, useMemo, useState } from 'react'
import { showErrorToast } from '../../../shared/utils/apiErrorMessage'
import {
  CrewTraining,
  crewTrainingAttachmentDownloadUrl,
  crewTrainingAttachmentViewUrl,
} from '../core/_requests'
import { useAuth } from '../../auth'
import SearchableSelect from '../../../shared/components/SearchableSelect'

type Props = {
  visible: boolean
  training: CrewTraining | null
  onClose: () => void
  onViewAttachment: (payload: { title: string; fileName: string; viewUrl: string; downloadUrl: string }) => void
}

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 2100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const CrewTrainingAttachmentsModal: FC<Props> = ({ visible, training, onClose, onViewAttachment }) => {
  const { auth } = useAuth()
  const token = auth?.auth?.jwt

  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [downloadingId, setDownloadingId] = useState<number | null>(null)

  useEffect(() => {
    if (!visible) return
    setCurrentPage(1)
    setRowsPerPage(10)
    setDownloadingId(null)
  }, [visible, training?.id])

  const attachments = useMemo(() => training?.attachments || [], [training])
  const total = attachments.length
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage))
  const start = (currentPage - 1) * rowsPerPage
  const pageItems = attachments.slice(start, start + rowsPerPage)

  const handleDownload = async (attachmentId: number, fileName: string) => {
    if (!training) return
    try {
      setDownloadingId(attachmentId)
      const url = crewTrainingAttachmentDownloadUrl(training.id, attachmentId)
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      })
      if (!res.ok) {
        throw new Error(`Download failed: ${res.status}`)
      }

      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = fileName || 'download'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(objectUrl)
    } catch (error: any) {
      console.error(error)
      showErrorToast(error, 'Download failed')
    } finally {
      setDownloadingId(null)
    }
  }

  if (!visible || !training) return null

  return (
    <div style={WRAP_STYLE} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className='bg-white rounded shadow-lg d-flex flex-column' style={{ width: '100%', maxWidth: 850, maxHeight: '90vh' }}>
        <div className='d-flex justify-content-between align-items-center border-bottom px-5 py-4'>
          <div>
            <h5 className='m-0'>Crew Training Attachments</h5>
            <small className='text-muted'>Crew: {training.crewName || '-'} | Total: {total}</small>
          </div>
          <button type='button' className='btn-close' onClick={onClose} />
        </div>

        <div className='p-5 flex-grow-1' style={{ overflowY: 'auto' }}>
          {total === 0 ? (
            <div className='text-muted text-center py-4'>No attachments found.</div>
          ) : (
            <div className='table-responsive'>
              <table className='table table-sm align-middle mb-0'>
                <thead>
                  <tr className='bg-light-primary'>
                    <th style={{ width: 70 }}>Sr/No</th>
                    <th>File Name</th>
                    <th className='text-end pe-6' style={{ width: 220 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((item, idx) => (
                    <tr key={item.id}>
                      <td>{start + idx + 1}</td>
                      <td>
                        <div className='text-truncate' style={{ maxWidth: '420px' }} title={item.fileName}>{item.fileName}</div>
                      </td>
                      <td className='text-end'>
                        <button
                          type='button'
                          className='btn btn-sm btn-light-primary me-2'
                          onClick={() =>
                            onViewAttachment({
                              title: item.fileName,
                              fileName: item.fileName,
                              viewUrl: crewTrainingAttachmentViewUrl(training.id, item.id),
                              downloadUrl: crewTrainingAttachmentDownloadUrl(training.id, item.id),
                            })
                          }
                        >
                          View
                        </button>
                        <button
                          type='button'
                          className='btn btn-sm btn-light-info'
                          onClick={() => handleDownload(item.id, item.fileName)}
                          disabled={downloadingId === item.id}
                        >
                          {downloadingId === item.id ? 'Downloading...' : 'Download'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className='d-flex justify-content-between align-items-center border-top px-5 py-4'>
          <div className='text-muted small'>
            Page {currentPage} of {totalPages}
          </div>
          <div className='btn-group'>
            <button
              type='button'
              className='btn btn-sm btn-light'
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <button
              type='button'
              className='btn btn-sm btn-light'
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
          <SearchableSelect
            className='form-select form-select-sm w-auto'
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value))
              setCurrentPage(1)
            }}
          >
            {[5, 10, 20, 50].map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </SearchableSelect>
        </div>
      </div>
    </div>
  )
}

export default CrewTrainingAttachmentsModal
